import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { ScopeAndSequenceLevel, WordId } from '@/common/types/scopeAndSequence';
import { LevelProgress } from '@/common/types/student';
import { getWordById } from '@/common/utils/scopeAndSequence';
import { GeneralGameState } from '@/game/GameComponents/PhaserGame/Types/IGeneralGameState';
import { HeartWordsState } from '@/game/GameComponents/PhaserGame/Types/IHeartWordsState';
import { MorphMasterState } from '@/game/GameComponents/PhaserGame/Types/IMorphMasterState';
import { MysticMatchState } from '@/game/GameComponents/PhaserGame/Types/IMysticMatchState';
import { PhonemeForgeState } from '@/game/GameComponents/PhaserGame/Types/IPhonemeForgeState';
import { SentenceBuilderState } from '@/game/GameComponents/PhaserGame/Types/ISentenceBuilderState';
import { WordRiftsState } from '@/game/GameComponents/PhaserGame/Types/IWordRiftsState';
import { WordSumsState } from '@/game/GameComponents/PhaserGame/Types/IWordSumsState';
import { WordWeaverState } from '@/game/GameComponents/PhaserGame/Types/IWordWeaverState';
import { Collision } from '@dnd-kit/core';

const gameDetails = [
  {
    id: 'mysticMatch',
    name: 'Mystic Match',
    instructions:
      'Listen to the sound and choose the letter, or letters, we use to write that sound.',
  },
  {
    id: 'wordWeaver',
    name: 'Word Weaver',
    instructions:
      'Listen to the word and then use the letters provided to build that word.',
  },
  {
    id: 'phonemeForge',
    name: 'Phoneme Forge',
    instructions: 'Swap letters to make new words.',
  },
  {
    id: 'sentenceBuilder',
    name: 'Sentence Builder',
    instructions:
      'Listen to the sentence and then arrange the words in the right order to make that sentence.',
  },
  {
    id: 'trickyWords',
    name: 'Heart Words',
    instructions:
      'Listen to the word and then choose that word from the options provided.',
  },
  {
    id: 'wordRifts',
    name: 'Word Rifts',
    instructions: 'Choose the correct word to finish the sentence.',
  },
  {
    id: 'wordSums',
    name: 'Word Sums',
    instructions: 'Build words using morphemes.',
  },
  {
    id: 'morphMaster',
    name: 'Morph Master',
    instructions: 'Swap morphemes to make new words.',
  },
];

export function getGameName(gameTitle: string) {
  const game = gameDetails.find((game) => game.id === gameTitle);
  return game?.name;
}

export function getGameInstructions(gameTitle: string) {
  const game = gameDetails.find((game) => game.id === gameTitle);
  return game?.instructions;
}

export function shuffleItems<T>(items: T[]): T[] {
  const a = items.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function getMixedGameItems<T extends string>(
  itemKey: keyof ScopeAndSequenceLevel,
  level: number,
  itemLimit: number,
  newItemRatio: number,
  preferMultiMorphemeWords: boolean = false,
): T[] {
  if (level < 1 || level > scopeAndSequence.levels.length) {
    throw new Error(`Invalid level: ${level}`);
  }

  const maybeFilterForMultiMorphemeWords = (items: T[]) => {
    if (!preferMultiMorphemeWords) return items;
    return items.filter((item) => {
      const word = getWordById(item as WordId);
      if (!word) return false;
      return word.morphemes.length > 1;
    });
  };

  // Find the most recent non-empty level to treat as current
  let currentLevelItems: Set<T> = new Set();
  let currentLevelIdx = level - 1;
  for (let i = level - 1; i >= 0; i--) {
    currentLevelItems = new Set(
      maybeFilterForMultiMorphemeWords(
        scopeAndSequence.levels[i][itemKey] as T[],
      ),
    );
    currentLevelIdx = i;
    if (currentLevelItems.size > 0) break;
  }

  const previousLevelsItems: Set<T> = new Set();
  for (let i = currentLevelIdx - 1; i >= 0; i--) {
    const levelItems = maybeFilterForMultiMorphemeWords(
      scopeAndSequence.levels[i][itemKey] as T[],
    );
    for (const item of levelItems) {
      if (!currentLevelItems.has(item)) previousLevelsItems.add(item);
    }
    if (previousLevelsItems.size + currentLevelItems.size >= itemLimit) {
      break;
    }
  }

  // Create a new Set for our final selection
  const items = new Set<T>();

  function pickAndRemoveUniqueItems(source: Set<T>, count: number): T[] {
    const pickedItems: T[] = [];
    const tempSource = [...source];

    while (pickedItems.length < count && tempSource.length > 0) {
      const randomIndex = Math.floor(Math.random() * tempSource.length);
      const candidate = tempSource.splice(randomIndex, 1)[0];

      if (!items.has(candidate)) {
        pickedItems.push(candidate);
      }
    }
    return pickedItems;
  }

  const totalAvailableItems = currentLevelItems.size + previousLevelsItems.size;

  if (totalAvailableItems <= itemLimit) {
    return shuffleItems([...currentLevelItems, ...previousLevelsItems]);
  }

  const newItemCount = Math.min(
    Math.ceil(itemLimit * newItemRatio),
    currentLevelItems.size,
  );

  // Add new items first
  pickAndRemoveUniqueItems(currentLevelItems, newItemCount).forEach((item) => {
    if (items.size < itemLimit) {
      items.add(item);
    }
  });

  // Calculate remaining space for old items
  const remainingSpace = itemLimit - items.size;

  // Add old items
  if (remainingSpace > 0) {
    pickAndRemoveUniqueItems(previousLevelsItems, remainingSpace).forEach(
      (item) => {
        if (items.size < itemLimit) {
          items.add(item);
        }
      },
    );
  }

  const finalRemainingSpace = itemLimit - items.size;

  if (finalRemainingSpace > 0) {
    const remainingCurrentItems = new Set(
      [...currentLevelItems].filter((item) => !items.has(item)),
    );
    pickAndRemoveUniqueItems(
      remainingCurrentItems,
      Math.min(finalRemainingSpace, remainingCurrentItems.size),
    ).forEach((item) => {
      if (items.size < itemLimit) {
        items.add(item);
      }
    });
  }

  return shuffleItems(Array.from(items));
}

type SpecificGameState =
  | MysticMatchState
  | WordWeaverState
  | PhonemeForgeState
  | SentenceBuilderState
  | HeartWordsState
  | WordRiftsState
  | WordSumsState
  | MorphMasterState;
export const calculateProgress = (
  generalGameState: GeneralGameState,
  specificGameState: SpecificGameState,
  maxProgress = 100,
) => {
  let correctAnswers = 0;
  let totalPossibleAnswers = 0;
  switch (generalGameState.currentGame.toLowerCase()) {
    case 'mysticmatch':
      const mysticMatchState = specificGameState as MysticMatchState;
      correctAnswers = Object.values(
        mysticMatchState.correctGraphemeCounts,
      ).reduce((total, count) => {
        return total + Math.min(count, 2);
      }, 0);
      totalPossibleAnswers =
        (generalGameState.gameData.mysticMatch?.length || 0) * 2;
      break;

    case 'wordweaver':
      const wordWeaverState = specificGameState as WordWeaverState;

      correctAnswers = wordWeaverState.usedWords.length;
      totalPossibleAnswers = generalGameState.gameData.wordWeaver?.length || 0;
      break;

    case 'phonemeforge':
      const phonemeForgeState = specificGameState as PhonemeForgeState;
      correctAnswers = phonemeForgeState.wordsCompleted.length;
      totalPossibleAnswers =
        (generalGameState.gameData.phonemeForge?.length || 0) - 1;
      break;

    case 'sentencebuilder':
      const sentenceBuilderState = specificGameState as SentenceBuilderState;
      correctAnswers = sentenceBuilderState.usedSentences.length;
      totalPossibleAnswers =
        generalGameState.gameData.sentenceBuilder?.length || 0;
      break;

    case 'trickywords':
      const trickyWordsState = specificGameState as HeartWordsState;
      correctAnswers = Object.values(
        trickyWordsState.correctHeartWordCount,
      ).reduce((total: number, count: any) => {
        return total + Math.min(Number(count), 2);
      }, 0);
      totalPossibleAnswers =
        (generalGameState.gameData.trickyWords?.length || 0) * 2;
      break;

    case 'wordrifts':
      const wordRiftsState = specificGameState as WordRiftsState;
      correctAnswers = wordRiftsState.usedWords.length;
      totalPossibleAnswers = generalGameState.gameData.wordRifts?.length || 0;
      break;

    case 'wordsums':
      const wordSumsState = specificGameState as WordSumsState;
      correctAnswers = wordSumsState.usedWords.length;
      totalPossibleAnswers = generalGameState.gameData.wordSums?.length || 0;
      break;

    case 'morphmaster':
      const morphMasterState = specificGameState as MorphMasterState;
      correctAnswers = morphMasterState.wordsCompleted.length;
      totalPossibleAnswers =
        (generalGameState.gameData.morphMaster?.length || 1) - 1;
      break;

    default:
      return 0; // Return 0 progress if game type is not recognized
  }

  if (totalPossibleAnswers === 0) return 0;
  const progress = (correctAnswers / totalPossibleAnswers) * maxProgress;
  return Math.min(progress, maxProgress); // Ensure progress does not exceed maxProgress
};

export function allGamesCompleted(progress: LevelProgress) {
  // Check if every game has been played this playthrough
  const { completedPlaythroughs, gamesProgress } = progress;
  return Object.values(gamesProgress).every(
    (game) => game.timesPlayed > completedPlaythroughs,
  );
}

// Finds the collision object with the highest 'value' (i.e. the closest one).
// Doesn't count an object if its id is in the 'exclude' array. Returns
// undefined if there are no collisions. Otherwise returns the id of the
// closest object.
export function findClosestCardFromCollisions(
  collisions: Collision[] | null,
  exclude: string[],
) {
  if (!collisions) return;
  let closestCardDistance;
  let closestCardId;
  for (const collision of collisions) {
    if (exclude.includes(collision.id as string) || !collision.data) continue;
    if (!closestCardDistance || collision.data.value > closestCardDistance) {
      closestCardDistance = collision.data.value;
      closestCardId = collision.id;
    }
  }
  return closestCardId;
}

/**
 * Calculate the score based on the accuracy and time taken.
 * The score is calculated using the formula:
 * `score = max(accuracy - k * log(timeTaken + 1), 0) * 100`,
 * where `k` is the drop-off coefficient.
 *
 * @param accuracy The accuracy of the activity as a percentage (0-100).
 * @param timeTaken The time taken to complete the attempt in seconds.
 * @param maxScoreCutOff The time after which the maximum score is no longer reachable in seconds (default is 45).
 *
 * @returns The calculated score as an integer.
 * */
export function getScore(
  accuracy: number,
  timeTaken: number,
  maxScoreCutOff = 45,
) {
  const k = 2; // Drop-off rate

  if (timeTaken <= maxScoreCutOff) {
    return Math.round(accuracy * 100);
  }

  const score = Math.round(
    Math.max(accuracy - k * Math.log(timeTaken - maxScoreCutOff + 1), 0) * 100,
  );

  return score;
}
